import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface ServiceFeeForm {
  country_name: string;
  seller_fee_percent: number;
  buyer_fee_percent: number;
  provider_fee_percent: number;
  requester_fee_percent: number;
  renter_fee_percent: number;
  rental_provider_fee_percent: number;
  verification_fee: number;
  verification_fee_currency: string;
  verification_fee_currency_symbol: string;
  start_date: string;
  end_date: string;
  in_use: boolean;
}

const ServiceFeeAdd: React.FC = () => {
  const navigate = useNavigate();
  const { adminRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ServiceFeeForm>({
    country_name: '',
    seller_fee_percent: 0,
    buyer_fee_percent: 0,
    provider_fee_percent: 0,
    requester_fee_percent: 0,
    renter_fee_percent: 0,
    rental_provider_fee_percent: 0,
    verification_fee: 0,
    verification_fee_currency: 'usd',
    verification_fee_currency_symbol: '$',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    in_use: true,
  });

  // Redirect if not super_admin
  if (adminRole !== 'super_admin') {
    navigate('/service-fees');
    return null;
  }

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      'usd': '$',
      'eur': '€',
      'gbp': '£',
      'aud': 'A$',
      'cad': 'C$',
    };
    return symbols[currency] || '$';
  };

  const getCountryCurrency = (country: string) => {
    const countryCurrencies: { [key: string]: string } = {
      'United States': 'usd',
      'Canada': 'cad',
      'United Kingdom': 'gbp',
      'Germany': 'eur',
      'France': 'eur',
      'Italy': 'eur',
      'Spain': 'eur',
      'Netherlands': 'eur',
      'Belgium': 'eur',
      'Austria': 'eur',
      'Australia': 'aud',
      'New Zealand': 'nzd',
      'Japan': 'jpy',
      'China': 'cny',
      'India': 'inr',
      'Brazil': 'brl',
      'Mexico': 'mxn',
      'South Africa': 'zar',
      'Switzerland': 'chf',
      'Sweden': 'sek',
      'Norway': 'nok',
      'Denmark': 'dkk',
      'Finland': 'eur',
      'Poland': 'pln',
      'Czech Republic': 'czk',
      'Hungary': 'huf',
      'Romania': 'ron',
      'Bulgaria': 'bgn',
      'Croatia': 'hrk',
      'Slovenia': 'eur',
      'Slovakia': 'eur',
      'Estonia': 'eur',
      'Latvia': 'eur',
      'Lithuania': 'eur',
      'Ireland': 'eur',
      'Portugal': 'eur',
      'Greece': 'eur',
      'Cyprus': 'eur',
      'Malta': 'eur',
      'Luxembourg': 'eur',
    };
    return countryCurrencies[country] || 'usd';
  };

  const handleInputChange = (field: keyof ServiceFeeForm, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-update currency and symbol when country changes
      if (field === 'country_name') {
        const currency = getCountryCurrency(value);
        newData.verification_fee_currency = currency;
        newData.verification_fee_currency_symbol = getCurrencySymbol(currency);
      }
      
      // Auto-update currency symbol when currency changes
      if (field === 'verification_fee_currency') {
        newData.verification_fee_currency_symbol = getCurrencySymbol(value);
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.country_name.trim()) {
        setError('Country name is required');
        setLoading(false);
        return;
      }

      if (formData.start_date === '') {
        setError('Start date is required');
        setLoading(false);
        return;
      }

      // Create the new service fee
      const { error: insertError } = await supabase
        .from('servicefee')
        .insert({
          ...formData,
          end_date: formData.end_date || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (insertError) {
        setError('Failed to create service fee');
        setLoading(false);
        return;
      }

      // Success - redirect to service fees page
      alert('Service fee created successfully!');
      navigate('/service-fees');
    } catch (err) {
      setError('Failed to create service fee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#00a67e] shadow-sm border-b border-[#008f6b]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Add New Service Fee</h2>
              <p className="text-green-100 mt-1">Create a new service fee configuration</p>
            </div>
            <button
              onClick={() => navigate('/service-fees')}
              className="flex items-center px-4 py-2 text-white bg-[#008f6b] rounded-lg hover:bg-[#007a5a] transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              Back to Service Fees
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Service Fee Configuration
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Fill in the details below to create a new service fee configuration
              </p>
            </div>

            <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Country Name */}
                <div>
                  <label htmlFor="country_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Country Name *
                  </label>
                  <select
                    id="country_name"
                    required
                    value={formData.country_name}
                    onChange={(e) => handleInputChange('country_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select a country</option>
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Italy">Italy</option>
                    <option value="Spain">Spain</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="Belgium">Belgium</option>
                    <option value="Austria">Austria</option>
                    <option value="Australia">Australia</option>
                    <option value="New Zealand">New Zealand</option>
                    <option value="Japan">Japan</option>
                    <option value="China">China</option>
                    <option value="India">India</option>
                    <option value="Brazil">Brazil</option>
                    <option value="Mexico">Mexico</option>
                    <option value="South Africa">South Africa</option>
                    <option value="Switzerland">Switzerland</option>
                    <option value="Sweden">Sweden</option>
                    <option value="Norway">Norway</option>
                    <option value="Denmark">Denmark</option>
                    <option value="Finland">Finland</option>
                    <option value="Poland">Poland</option>
                    <option value="Czech Republic">Czech Republic</option>
                    <option value="Hungary">Hungary</option>
                    <option value="Romania">Romania</option>
                    <option value="Bulgaria">Bulgaria</option>
                    <option value="Croatia">Croatia</option>
                    <option value="Slovenia">Slovenia</option>
                    <option value="Slovakia">Slovakia</option>
                    <option value="Estonia">Estonia</option>
                    <option value="Latvia">Latvia</option>
                    <option value="Lithuania">Lithuania</option>
                    <option value="Ireland">Ireland</option>
                    <option value="Portugal">Portugal</option>
                    <option value="Greece">Greece</option>
                    <option value="Cyprus">Cyprus</option>
                    <option value="Malta">Malta</option>
                    <option value="Luxembourg">Luxembourg</option>
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    required
                    value={formData.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    value={formData.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Status */}
                <div>
                  <label htmlFor="in_use" className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    id="in_use"
                    value={formData.in_use ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('in_use', e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Fee Percentages */}
              <div className="mt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Fee Percentages</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="seller_fee_percent" className="block text-sm font-medium text-gray-700 mb-2">
                      Seller Fee (%)
                    </label>
                    <input
                      type="number"
                      id="seller_fee_percent"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.seller_fee_percent}
                      onChange={(e) => handleInputChange('seller_fee_percent', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="buyer_fee_percent" className="block text-sm font-medium text-gray-700 mb-2">
                      Buyer Fee (%)
                    </label>
                    <input
                      type="number"
                      id="buyer_fee_percent"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.buyer_fee_percent}
                      onChange={(e) => handleInputChange('buyer_fee_percent', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="provider_fee_percent" className="block text-sm font-medium text-gray-700 mb-2">
                      Provider Fee (%)
                    </label>
                    <input
                      type="number"
                      id="provider_fee_percent"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.provider_fee_percent}
                      onChange={(e) => handleInputChange('provider_fee_percent', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="requester_fee_percent" className="block text-sm font-medium text-gray-700 mb-2">
                      Requester Fee (%)
                    </label>
                    <input
                      type="number"
                      id="requester_fee_percent"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.requester_fee_percent}
                      onChange={(e) => handleInputChange('requester_fee_percent', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="renter_fee_percent" className="block text-sm font-medium text-gray-700 mb-2">
                      Renter Fee (%)
                    </label>
                    <input
                      type="number"
                      id="renter_fee_percent"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.renter_fee_percent}
                      onChange={(e) => handleInputChange('renter_fee_percent', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="rental_provider_fee_percent" className="block text-sm font-medium text-gray-700 mb-2">
                      Rental Provider Fee (%)
                    </label>
                    <input
                      type="number"
                      id="rental_provider_fee_percent"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.rental_provider_fee_percent}
                      onChange={(e) => handleInputChange('rental_provider_fee_percent', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Verification Fee */}
              <div className="mt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Verification Fee</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="verification_fee" className="block text-sm font-medium text-gray-700 mb-2">
                      Amount
                    </label>
                    <input
                      type="number"
                      id="verification_fee"
                      step="0.01"
                      min="0"
                      value={formData.verification_fee}
                      onChange={(e) => handleInputChange('verification_fee', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="verification_fee_currency" className="block text-sm font-medium text-gray-700 mb-2">
                      Currency (Auto-selected)
                    </label>
                    <input
                      type="text"
                      id="verification_fee_currency"
                      value={formData.verification_fee_currency.toUpperCase()}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-8 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/service-fees')}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#10b981] hover:bg-[#059669] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#10b981] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create Service Fee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServiceFeeAdd;
